/**
 * Collection Description Toggle
 * Handles read more/less functionality for collection descriptions on mobile devices
 */

class CollectionDescriptionToggle {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.content = wrapper.querySelector('.collection-description-content');
    this.toggle = wrapper.querySelector('.collection-description-toggle');
    this.icon = wrapper.querySelector('.collection-description-toggle-icon');
    
    this.isExpanded = false;
    this.isMobile = window.innerWidth <= 768;
    
    this.init();
  }
  
  init() {
    if (!this.content || !this.toggle) return;
    
    // Set initial state
    this.updateState();
    
    // Bind events
    this.toggle.addEventListener('click', this.handleToggle.bind(this));
    window.addEventListener('resize', this.handleResize.bind(this));
    
    // Set up initial accessibility attributes
    this.setupAccessibility();
  }
  
  setupAccessibility() {
    // Generate unique IDs for ARIA relationship
    const contentId = `collection-desc-${Math.random().toString(36).substr(2, 9)}`;
    this.content.id = contentId;
    
    // Set ARIA attributes
    this.toggle.setAttribute('aria-controls', contentId);
    this.toggle.setAttribute('aria-expanded', this.isExpanded.toString());
    
    // Add screen reader text
    const srText = document.createElement('span');
    srText.className = 'visually-hidden';
    srText.textContent = ' collection description';
    this.toggle.appendChild(srText);
  }
  
  handleToggle(event) {
    event.preventDefault();
    
    if (!this.isMobile) return;
    
    this.isExpanded = !this.isExpanded;
    this.updateState();
    
    // Announce change to screen readers
    this.announceChange();
  }
  
  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 768;
    
    // If switching from mobile to desktop, ensure expanded state
    if (wasMobile && !this.isMobile) {
      this.isExpanded = true;
      this.updateState();
    }
    // If switching from desktop to mobile, reset to collapsed
    else if (!wasMobile && this.isMobile) {
      this.isExpanded = false;
      this.updateState();
    }
  }
  
  updateState() {
    // Update CSS classes
    this.content.classList.toggle('is-collapsed', !this.isExpanded && this.isMobile);
    this.content.classList.toggle('is-expanded', this.isExpanded || !this.isMobile);
    this.toggle.classList.toggle('is-expanded', this.isExpanded);
    
    // Update button text
    this.updateButtonText();
    
    // Update ARIA attributes
    this.toggle.setAttribute('aria-expanded', (this.isExpanded || !this.isMobile).toString());
    
    // Hide/show toggle button based on screen size
    this.toggle.style.display = this.isMobile ? 'inline-flex' : 'none';
  }
  
  updateButtonText() {
    const textSpan = this.toggle.querySelector('.collection-description-toggle-text');
    if (textSpan) {
      textSpan.textContent = this.isExpanded ? 'Read Less' : 'Read More';
    }
  }
  
  announceChange() {
    // Create a temporary element to announce the change to screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'visually-hidden';
    announcement.textContent = this.isExpanded ? 
      'Collection description expanded' : 
      'Collection description collapsed';
    
    document.body.appendChild(announcement);
    
    // Remove the announcement after a short delay
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }
  
  // Check if content actually needs truncation
  static shouldInitialize(wrapper) {
    const content = wrapper.querySelector('.collection-description-content');
    if (!content) return false;
    
    // Only initialize on mobile if content is tall enough to warrant truncation
    if (window.innerWidth <= 768) {
      const contentHeight = content.scrollHeight;
      return contentHeight > 120; // Match the CSS max-height
    }
    
    return true; // Always initialize for responsive behavior
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const wrappers = document.querySelectorAll('.collection-description-wrapper');
  
  wrappers.forEach(wrapper => {
    if (CollectionDescriptionToggle.shouldInitialize(wrapper)) {
      new CollectionDescriptionToggle(wrapper);
    }
  });
});

// Re-initialize on AJAX page loads (for themes that use AJAX navigation)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.collection-description-wrapper')) {
    const wrappers = event.target.querySelectorAll('.collection-description-wrapper');
    wrappers.forEach(wrapper => {
      if (CollectionDescriptionToggle.shouldInitialize(wrapper)) {
        new CollectionDescriptionToggle(wrapper);
      }
    });
  }
});

// Export for potential external use
window.CollectionDescriptionToggle = CollectionDescriptionToggle; 